import { useState } from "react";
import { t } from "@chuzi/shared/config";
import type { RealmId } from "@chuzi/shared/types";

interface CreateFabProps {
  realm: RealmId;
  onClick: () => void;
  disabled?: boolean;
  /** When true, skip absolute positioning (for grouped bottom-left controls). */
  embedded?: boolean;
  /** Compact mode for inline usage inside a toolbar. Smaller size, no label. */
  compact?: boolean;
}

export function CreateFab({ realm, onClick, disabled, embedded, compact }: CreateFabProps) {
  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false);

  const isWilds = realm === "wilds";
  const accent = isWilds ? "rgba(120,200,120," : "rgba(126,184,255,";
  const borderColor = `${accent}${hovered ? "0.7)" : "0.45)"}`;
  const bg = isWilds ? "rgba(20,40,20,0.92)" : "rgba(10,16,32,0.92)";
  const glowColor = `${accent}0.4)`;
  const iconColor = isWilds ? "#78c878" : "#7eb8ff";
  const labelColor = isWilds ? "rgba(160,220,160,0.7)" : "rgba(126,184,255,0.7)";
  const ringColor1 = isWilds ? "#78c878" : "#7eb8ff";
  const ringColor2 = isWilds ? "#2a6a2a" : "#1a4a8a";

  const outerSize = compact ? 52 : 100;
  const btnSize = compact ? 44 : 90;
  const iconSize = compact ? 24 : 40;

  return (
    <div
      style={{
        ...(embedded
          ? {}
          : {
              position: "absolute",
              bottom: 24,
              left: 24,
              zIndex: 110,
            }),
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: compact ? 0 : 6,
      }}
    >
      <div
        style={{
          position: "relative",
          width: outerSize,
          height: outerSize,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* Accretion disk ring — rotating conic gradient */}
        <div
          className="accretion-ring"
          style={{
            position: "absolute",
            inset: -2,
            borderRadius: "50%",
            background: `conic-gradient(from 0deg, ${ringColor1}, ${ringColor2}, transparent, ${ringColor1}, ${ringColor2}, transparent, ${ringColor1})`,
            opacity: disabled ? 0.15 : 0.65,
            animation: disabled ? "none" : "accretionSpin 8s linear infinite",
            filter: `blur(1.5px)`,
            pointerEvents: "none",
          }}
        />

        {/* Inner mask to make it a ring */}
        <div
          style={{
            position: "absolute",
            inset: 3,
            borderRadius: "50%",
            background: isWilds ? "rgba(4,7,4,0.95)" : "rgba(4,7,13,0.95)",
            pointerEvents: "none",
          }}
        />

        <button
          type="button"
          aria-label={t(realm, "menu_create_story")}
          onClick={() => {
            setPressed(true);
            setTimeout(() => setPressed(false), 150);
            onClick();
          }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          disabled={disabled}
          style={{
            position: "relative",
            width: btnSize,
            height: btnSize,
            borderRadius: "50%",
            border: `1px solid ${borderColor}`,
            background: bg,
            padding: 0,
            cursor: disabled ? "default" : "pointer",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            backdropFilter: "blur(8px)",
            boxShadow: hovered
              ? `0 0 24px ${glowColor}, 0 0 48px ${accent}0.2), 0 0 0 2px ${accent}0.3)`
              : `0 0 12px ${accent}0.25), 0 0 24px ${accent}0.12)`,
            transition:
              "box-shadow 200ms ease, transform 150ms ease, border-color 200ms ease",
            transform: pressed
              ? "scale(0.94)"
              : hovered
                ? "scale(1.06)"
                : "none",
            opacity: disabled ? 0.4 : 1,
            animation: disabled ? "none" : "accretionBreathe 2.4s ease-in-out infinite",
          }}
        >
          <svg
            width={iconSize}
            height={iconSize}
            viewBox="0 0 40 40"
            fill="none"
            style={{
              filter: `drop-shadow(0 0 ${hovered ? "8" : "4"}px ${glowColor})`,
              transition: "filter 200ms ease",
            }}
          >
            <line
              x1="20"
              y1="8"
              x2="20"
              y2="32"
              stroke={iconColor}
              strokeWidth="2.5"
              strokeLinecap="round"
            />
            <line
              x1="8"
              y1="20"
              x2="32"
              y2="20"
              stroke={iconColor}
              strokeWidth="2.5"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      {!compact && (
        <span
          style={{
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: 2,
            color: labelColor,
            textTransform: "uppercase",
            textShadow: `0 0 8px ${accent}0.4), 0 1px 4px rgba(0,0,0,0.6)`,
            pointerEvents: "none",
            userSelect: "none",
            animation: disabled ? "none" : "accretionLabelPulse 2.4s ease-in-out infinite",
          }}
        >
          {t(realm, "create_button")}
        </span>
      )}

      <style>{`
        @keyframes accretionSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes accretionBreathe {
          0%, 100% { box-shadow: 0 0 12px ${accent}0.25), 0 0 24px ${accent}0.12); }
          50% { box-shadow: 0 0 20px ${accent}0.4), 0 0 40px ${accent}0.2); }
        }
        @keyframes accretionLabelPulse {
          0%, 100% { opacity: 0.7; }
          50% { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
